// De dónde salen las paredes de un modelo.
//
// Dos fuentes, y el orden importa:
//
// 1. **El catálogo ya medido** (`medidos/`, que GENERA `planta-tool/publicar.py`
//    — por eso no hay lista escrita a mano: agregar un modelo es volver a
//    correr el barrido). Es un import dinámico: abre al instante, sin red, y
//    sólo trae modelos que pasaron el control contra el área que rotula su
//    propio plano.
// 2. **El convertidor como servicio** (`planta-tool/function_app.py` detrás de
//    `POST /api/planta/convertir`), hermano del sidecar que convierte el PDF del
//    sitio en el SVG de lotes: mismo molde, misma razón —.NET no parsea PDFs,
//    los manda por HTTP. Es lo que le quita a la pantalla el límite de abrir
//    sólo los 32 modelos ya medidos.
//
// Por qué no sólo el servicio: el catálogo no cuesta un viaje ni un segundo de
// espera, y ya está juzgado. El servicio es para el croquis que nadie midió —
// el caso del fin de semana, cuando no hay arquitectura en la oficina.
//
// Lo que el servicio NO hace es inventar: si la lámina no se puede leer con
// confianza contesta 400 con el motivo, y eso hay que mostrarlo. Mejor «esta
// lámina no tiene cotas» que un editor que abre una casa que no es.

import type { ModeloPlantaCrudo } from './tipos';
import { MEDIDOS } from './medidos/indice';
import { api } from '@/api/client';

/** Sin extensión y sin may/min: el blob a veces sirve el mismo croquis con
 *  sufijo de versión, y el nombre base es lo que identifica al modelo. */
const raiz = (s: string) => s.replace(/\.pdf$/i, '').toLowerCase();

/** La clave del croquis que identifica al modelo, o null si no está medido. */
function clave(planoUrl: string | null): string | null {
  if (!planoUrl) return null;
  const archivo = decodeURIComponent(planoUrl.split('/').pop() ?? '');
  if (MEDIDOS[archivo]) return archivo;
  const r = raiz(archivo);
  return Object.keys(MEDIDOS).find((k) => r.startsWith(raiz(k))) ?? null;
}

/** ¿Está en el catálogo ya medido? Sigue decidiendo si el botón «Planta»
 *  aparece junto a un modelo del que sólo tenemos la URL del plano: para
 *  medirlo en vivo haría falta el archivo, no un link. */
export function tienePlanta(planoUrl: string | null): boolean {
  return clave(planoUrl) != null;
}

export async function cargarPlanta(
  planoUrl: string | null,
): Promise<ModeloPlantaCrudo | null> {
  const k = clave(planoUrl);
  if (!k) return null;
  return (await MEDIDOS[k]()).default;
}

/** El archivo que alguien soltó en la pantalla → sus paredes.
 *
 *  Atajo por el catálogo cuando el nombre coincide (sin subir nada), y si no,
 *  al convertidor. Lo que tira es el `ApiError` tal cual: su `status` distingue
 *  el 400 «no la puedo leer, y este es el motivo» del 502 «el convertidor no
 *  contesta», y quien llama necesita esa diferencia para decir algo útil. */
export async function cargarPlantaDeArchivo(f: File): Promise<ModeloPlantaCrudo> {
  return cargarPlantaDeBytes(f, f.name);
}

/** Lo mismo, con el nombre aparte.
 *
 *  Es la vía del croquis que bajó del blob por el backend: ahí los bytes no
 *  traen nombre —son una respuesta HTTP, no un archivo que alguien eligió— y el
 *  nombre lo dice el catálogo de modelos. El nombre no es decoración: es lo que
 *  decide si la planta se resuelve del catálogo medido o si hay que mandarla a
 *  medir, y es lo que el convertidor devuelve como procedencia. */
export async function cargarPlantaDeBytes(
  bytes: Blob,
  nombre: string,
): Promise<ModeloPlantaCrudo> {
  const k = clave(nombre);
  if (k) return (await MEDIDOS[k]()).default;
  return api.planta.convertirBytes(bytes, nombre);
}
